import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getdata, updatedata, deletedata, uploadfiledata, downloadfile } from '../../util/global';
import TableBatch from './TableBatch';
import UploadForm from './UploadForm';

const MAXFILESIZE = 15 * 1024 * 1024;

const EMPTYBATCH = { _id: null, clientname: "", period: "", file: null };

const PrudentialDashboard = () => {
  const navigate = useNavigate();
  const [batchdata, setbatchdata] = useState<any[]>([]);
  const [linedata, setlinedata] = useState<any[]>([]);
  const [mode, setmode] = useState<string | null>(null);
  const [formdata, setformdata] = useState<any>({ ...EMPTYBATCH });
  const [saving, setsaving] = useState(false);

  const sortedbatchdata = useMemo(() => {
    const sorted = batchdata.slice();
    sorted.sort((a, b) => String(b.createdtimestamp).localeCompare(String(a.createdtimestamp)));
    return sorted;
  }, [batchdata]);

  const countmap = useMemo(() => {
    const map: any = {};
    for (let i = 0; i < linedata.length; i++) {
      const line = linedata[i];
      if (!map[line._batchid]) {
        map[line._batchid] = { lines: 0, pending: 0, approved: 0, flagged: 0 };
      }
      map[line._batchid].lines++;
      if (line.reviewstatus == "pending") map[line._batchid].pending++;
      if (line.reviewstatus == "approved") map[line._batchid].approved++;
      if (line.reviewstatus == "flagged") map[line._batchid].flagged++;
    }
    return map;
  }, [linedata]);

  const fetchdata = async () => {
    const batches = await getdata({ filter: { datatype: "prudentialbatch" } });
    if (Array.isArray(batches)) setbatchdata(batches);
    const lines = await getdata({ filter: { datatype: "prudentialline" } });
    if (Array.isArray(lines)) setlinedata(lines);
  };

  const openupload = () => {
    setformdata({ ...EMPTYBATCH });
    setmode("upload");
  };

  const selectfile = (file: any) => {
    if (!file) {
      setformdata({ ...formdata, file: null });
      return;
    }
    if (file.type != "application/pdf") {
      alert("Only PDF files can be uploaded");
      return;
    }
    if (file.size > MAXFILESIZE) {
      alert("The PDF is " + Math.round(file.size / 1024 / 1024) + " MB. The maximum is 15 MB.");
      return;
    }
    setformdata({ ...formdata, file: file });
  };

  const savebatch = async () => {
    const clientname = formdata.clientname.trim();
    if (!clientname || !formdata.period || !formdata.file) {
      alert("Client, period and a PDF file are required");
      return;
    }
    setsaving(true);
    try {
      const uploaded = await uploadfiledata({
        files: [formdata.file],
        _adminuserid: window.APPSETTING.adminuserid,
        _userid: window.APPUSER.user.userid,
        appname: window.APPSETTING.appname,
        filetype: "prudentialpack",
        customfilenames: formdata.file.name,
      });
      const fileid = uploaded && uploaded.results && uploaded.results[0] ? uploaded.results[0].upsertedId : null;
      if (!fileid) {
        alert("Upload failed: " + (uploaded && uploaded.error ? uploaded.error : "no file id returned"));
        return;
      }
      const saved = await updatedata({ data: {
        datatype: "prudentialbatch",
        clientname: clientname,
        period: formdata.period,
        filename: formdata.file.name,
        _fileid: fileid,
        status: "uploaded",
        createdtimestamp: new Date(),
      } });
      const batchid = saved && saved[0] ? saved[0].id : null;
      if (!batchid) {
        alert("Batch could not be saved");
        return;
      }
      await getdata({ batchid: batchid }, "/prudentialprocess");
      setmode(null);
      await fetchdata();
    } finally {
      setsaving(false);
    }
  };

  const openbatch = (batch: any) => {
    navigate("/review?batchid=" + batch._id);
  };

  const exportbatch = async (batch: any) => {
    const result = await downloadfile({ batchid: batch._id }, "/prudentialexport");
    if (result && result.error) alert(result.error);
  };

  const deletebatch = async (batch: any) => {
    if (!confirm("Delete batch " + batch.clientname + " " + batch.period + " and all its lines?")) return;
    await deletedata({ filter: { datatype: "prudentialline", _batchid: batch._id } }, null);
    await deletedata({ filter: { datatype: "prudentialbatch", _id: batch._id } }, null);
    if (batch._fileid) {
      await deletedata({ filter: { datatype: "filedata", dbcollection: "appfiledata", _id: batch._fileid } }, null);
    }
    await fetchdata();
  };

  useEffect(() => { fetchdata(); }, []);

  // Poll every 5 seconds while any batch is processing.
  useEffect(() => {
    let processing = false;
    for (let i = 0; i < batchdata.length; i++) {
      if (batchdata[i].status == "processing") processing = true;
    }
    if (!processing) return;
    const timer = setTimeout(() => { fetchdata(); }, 5000);
    return () => clearTimeout(timer);
  }, [batchdata]);

  return (
    <>
      <div className="page-header">
        <h2>Batches</h2>
        <button className="btn-primary" onClick={openupload}>Upload client pack</button>
      </div>
      {batchdata.length == 0 && <p className="page-note">Upload a client pack to start</p>}

      <TableBatch batchdata={sortedbatchdata} countmap={countmap} openbatch={openbatch} exportbatch={exportbatch} deletebatch={deletebatch} />

      <UploadForm
        mode={mode}
        setmode={setmode}
        formdata={formdata}
        setformdata={setformdata}
        selectfile={selectfile}
        savebatch={savebatch}
        saving={saving}
      />
    </>
  );
};

export default PrudentialDashboard;
